import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Clock,
  Euro,
  FileText,
  HeartPulse,
  Link as LinkIcon,
  ReceiptText,
  CalendarDays,
  Wrench,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import type {
  MyShortcut,
  MyShortcutList,
  ShortcutColor,
  ShortcutDestination,
  ShortcutIcon,
} from '@up2d8/contracts';
import { SectionHead, cn, useToast } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { safeNavigationUrl } from '../../shared/safe-url';

/**
 * De tegelrij op het beginscherm (Stap 19).
 *
 * Voor een medewerker zonder bedrijfse-mail is dit vaak het nuttigste stuk van de app: zijn
 * loonbrief, zijn verlofsaldo en zijn uurregistratie staan in drie systemen met elk een eigen
 * aanmelding, en één tegel zet hem met zijn eigen personeelsnummer op de juiste pagina.
 *
 * De tegels verdwijnen volledig als er geen zijn — een lege rij met een kopje "Snelkoppelingen"
 * boven een tijdlijn is ruimte die niets doet.
 */

/** De iconenbibliotheek. Sinds FBK-8 kan een tegel ook een eigen icoon hebben (`iconUrl`). */
const ICONS: Record<
  ShortcutIcon,
  ComponentType<{ className?: string; 'aria-hidden'?: boolean }>
> = {
  payslip: ReceiptText,
  calendar: CalendarDays,
  clock: Clock,
  document: FileText,
  euro: Euro,
  health: HeartPulse,
  link: LinkIcon,
  tools: Wrench,
};

/**
 * De kleur van het tegeltje, als paar: een zacht vlak met een donker icoon erop.
 *
 * Tint als achtergrond en de verzadigde kleur als icoon, nooit andersom — een vol gekleurd vlak
 * van 40 px naast zeven andere maakt een rooster dat schreeuwt, en dan valt niets meer op. De
 * donkere variant op de tint haalt bovendien de contrasteis (`docs/design/a11y.md`), wat een wit
 * icoon op een tint nooit zou doen.
 *
 * De kleur staat nooit alleen: onder elke tegel staat het label. Kleur is hier een hulp bij het
 * terugvinden, geen drager van betekenis.
 */
const COLORS: Record<ShortcutColor, string> = {
  // De acht paren uit §8 van de replica-spec, letterlijk. Waar de mockup een tokenwaarde gebruikt,
  // staat het token; vier voorgrondkleuren bestaan alleen hier (`#0891A1`, `#15803D`, `#B85B1F`,
  // `#64748B`) en staan daarom als waarde — met de tint ernaast uit de referentie-CSS.
  neutral: 'bg-[#EEF1F5] text-[#64748B]',
  primary: 'bg-brand-primary-tint text-brand-primary-dark',
  cyan: 'bg-brand-cyan-tint text-[#0891A1]',
  dark: 'bg-brand-dark-blue-tint text-brand-dark-blue',
  orange: 'bg-brand-orange-tint text-brand-orange-deep',
  success: 'bg-success-tint text-[#15803D]',
  warning: 'bg-[#FEF3E4] text-[color:var(--brand-orange-strong)]',
  danger: 'bg-danger-tint text-danger',
};

/**
 * Hoeveel tegels er op het beginscherm passen voordat ze de tijdlijn wegduwen.
 *
 * Acht is twee rijen van vier. Daarboven staat "Alles": een rooster dat doorloopt tot onder de
 * vouw is geen rooster meer maar een lijst, en dan is de tijdlijn — de reden dat de app bestaat —
 * niet meer te zien zonder scrollen. Dat gebeurde: met twaalf tegels stond de feed buiten beeld.
 */
const MAX_ON_HOME = 8;

export function AppShortcuts({ homeOnly = true }: { homeOnly?: boolean }) {
  const { t } = useTranslation();
  const { api, has } = useSession();

  const shortcuts = useQuery({
    // Altijd de volledige lijst: Home moet weten of er tegels zijn die níet op Home staan, anders
    // is er geen weg naar "Alles" en zijn die tegels onbereikbaar.
    queryKey: ['my-shortcuts'],
    enabled: has('shortcuts.shortcut.use'),
    // Staat de module uit, dan blijft dat zo zolang de sessie duurt.
    retry: false,
    queryFn: async (): Promise<MyShortcut[]> => {
      const { data, error } = await api.GET('/api/v1/me/shortcuts', {
        params: { query: {} },
      });
      if (error || !data) return [];
      return (data as MyShortcutList).items;
    },
  });

  const all = shortcuts.data ?? [];
  if (!has('shortcuts.shortcut.use') || all.length === 0) return null;

  const items = homeOnly ? all.filter((shortcut) => shortcut.showOnHome) : all;
  const shown = homeOnly ? items.slice(0, MAX_ON_HOME) : items;
  const hidden = all.length > shown.length;

  return (
    <section aria-labelledby="shortcuts-heading" data-testid="shortcut-row">
      {homeOnly ? (
        <SectionHead
          id="shortcuts-heading"
          title={t('shortcuts.heading')}
          action={
            /*
              Twee verschillende wegen, en nooit samen (replica-spec §8).

              "Alles" is er zodra er meer tegels zijn dan er passen — anders zijn er tegels die
              niemand ziet, en dat is erger dan een lange lijst. "Aanpassen" is er alleen voor wie
              de tegels mág aanpassen: een link naar een beheerpagina die met een `403` eindigt,
              is een belofte die de app niet nakomt.

              Staat er niets aan te passen en past alles, dan staat er niets. De kop draagt dan
              alleen zijn titel, zoals in de mockup bij een rij van vier.
            */
            hidden ? (
              <Link to="/app/shortcuts" className="text-brand-primary">
                {t('common.all')}
              </Link>
            ) : has('shortcuts.shortcut.manage') ? (
              <Link to="/admin/shortcuts" className="text-brand-primary">
                {t('shortcuts.customise')}
              </Link>
            ) : null
          }
        />
      ) : (
        <h2 id="shortcuts-heading" className="sr-only">
          {t('shortcuts.heading')}
        </h2>
      )}
      <div className="grid grid-cols-4 gap-2.5 full:grid-cols-8">
        {shown.map((shortcut) => (
          <ShortcutTile key={shortcut.id} shortcut={shortcut} />
        ))}
      </div>
    </section>
  );
}

/**
 * Eén tegel.
 *
 * Een knop en geen link, en dat is een gecorrigeerde opzet. De eerste versie was een `<a href>`
 * naar onze eigen API, die met een `302` zou omleiden. Dat werkt niet: zo'n link is een navigatie
 * van de browser, en die stuurt geen `Authorization`-header mee — het toegangstoken leeft in het
 * geheugen van de app en niet in een cookie. Elke tik gaf een `401`.
 *
 * Nu vraagt deze knop de bestemming op mét token, en navigeert daarna zelf. Het tabblad wordt
 * **synchroon** geopend, vóór het wachten: een `window.open` ná een `await` wordt door
 * popupblokkers tegengehouden, omdat de gebruikersaanraking dan voorbij is. Lukt het openen niet,
 * dan gaat de navigatie in hetzelfde tabblad — liever de app verlaten dan niets doen.
 *
 * **Zonder `noopener` in de features**, en dat is geen slordigheid: met die vlag geeft
 * `window.open` volgens de specificatie `null` terug, en dan is er geen tabblad om de URL in te
 * zetten. De eerste versie deed dat wél en navigeerde daardoor altijd in hetzelfde tabblad. De
 * bescherming tegen reverse tabnabbing gebeurt nu met `tab.opener = null`, vóór het navigeren.
 */
function ShortcutTile({ shortcut }: { shortcut: MyShortcut }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const Icon = ICONS[shortcut.icon] ?? LinkIcon;

  const open = useMutation({
    mutationFn: async (tab: Window | null) => {
      const { data, error } = await api.POST('/api/v1/shortcuts/{shortcutId}/open', {
        params: { path: { shortcutId: shortcut.id } },
      });
      if (error || !data) {
        tab?.close();
        throw error ?? new Error('Openen mislukt');
      }

      const url = safeNavigationUrl((data as ShortcutDestination).url);
      if (!url) {
        tab?.close();
        throw new Error('Onveilig adres');
      }
      if (tab) {
        // De terugverwijzing doorsnijden vóór het navigeren: anders houdt het doelsysteem via
        // `window.opener` een greep op dit venster (reverse tabnabbing). Dit kan alleen zolang het
        // tabblad nog `about:blank` is — daarna is het een andere herkomst.
        tab.opener = null;
        tab.location.href = url;
      } else {
        // Popup geblokkeerd: dan in hetzelfde tabblad. Liever de app verlaten dan niets doen.
        window.location.assign(url);
      }
    },
    onError: () => {
      toast.show({ tone: 'danger', title: t('shortcuts.openFailed') });
    },
  });

  return (
    <button
      type="button"
      data-touch-target
      data-testid="shortcut-tile"
      disabled={open.isPending}
      onClick={() => open.mutate(window.open('', '_blank'))}
      className="flex min-h-touch flex-col items-center gap-2.5 rounded-lg border border-border-light bg-surface px-2.5 py-4 text-center shadow-sm transition-all hover:-translate-y-0.5 hover:border-brand-primary-tint-deep hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:opacity-60"
    >
      <span
        aria-hidden
        data-tile-icon
        className={cn(
          'flex h-11 w-11 shrink-0 items-center justify-center rounded-md',
          COLORS[shortcut.color] ?? COLORS.neutral,
        )}
      >
        {shortcut.iconUrl ? (
          // Het eigen icoon (FBK-8) op een witte tegel: een logo is voor wit gemaakt.
          <img
            src={shortcut.iconUrl}
            alt=""
            data-testid="shortcut-tile-image"
            className="h-full w-full rounded-md bg-white object-contain p-1"
          />
        ) : (
          <Icon className="h-[22px] w-[22px]" />
        )}
      </span>
      <span className="line-clamp-2 font-heading text-support font-medium leading-[1.3] text-text-primary desk:text-small">
        {shortcut.label}
      </span>
    </button>
  );
}
